// src/pages/CourseMaterials.jsx - COMPLETE UPDATED VERSION
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, BookOpen, User, Download, Filter, FileText, Calendar,
  Eye, Share2, Star, FileDown, FileSearch, FileCheck, ChevronDown, ChevronUp,
  FileImage, Maximize2, X, Image as ImageIcon, File
} from 'lucide-react';
import { motion } from 'framer-motion';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import Button from '../components/common/Button';
import { publicAPI } from '../services/api';

const CourseMaterials = () => {
  const { schoolId, departmentId, year, courseId, teacherId } = useParams();
  const navigate = useNavigate();
  
  const [course, setCourse] = useState(null);
  const [teacher, setTeacher] = useState(null);
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [downloading, setDownloading] = useState({});
  const [previewing, setPreviewing] = useState({});
  const [expandedMaterials, setExpandedMaterials] = useState({});
  const [showImageModal, setShowImageModal] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        const [courseRes, materialsRes] = await Promise.all([
          publicAPI.getCourseById(courseId),
          publicAPI.getMaterials({ 
            course: courseId, 
            teacher: teacherId
          })
        ]);

        if (courseRes.success) setCourse(courseRes.data);
        else setError('Course not found');

        if (materialsRes.success) {
          const fetchedMaterials = materialsRes.data || [];
          setMaterials(fetchedMaterials);
          
          if (fetchedMaterials.length > 0 && fetchedMaterials[0].teacher) {
            setTeacher(fetchedMaterials[0].teacher);
          }
        }

      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load materials');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [courseId, teacherId]);

  const filteredMaterials = materials.filter(material => {
    if (filterType !== 'all' && material.type !== filterType) return false;
    return true;
  });

  const handleDownload = async (material) => {
    if (!material._id) return;
    
    setDownloading(prev => ({ ...prev, [material._id]: true }));

    try {
      const downloadUrl = publicAPI.getDownloadUrl(material._id);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', material.file?.originalName || material.title);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      await publicAPI.trackDownload(material._id);

      // Update local state
      setMaterials(prev => prev.map(m => 
        m._id === material._id 
          ? { ...m, downloads: (m.downloads || 0) + 1 }
          : m
      ));

      // Show notification
      if (window.addDownloadNotification) {
        window.addDownloadNotification(material);
      }

    } catch (e) {
      console.error('Download failed:', e);
      alert("Download failed.");
    } finally {
      setTimeout(() => {
        setDownloading(prev => ({ ...prev, [material._id]: false }));
      }, 2000);
    }
  };

  const handlePreview = async (material) => {
    if (!material._id) return;
    
    const canPreview = publicAPI.canPreview(material);
    if (!canPreview) {
      alert('Preview not available for this file type. Please download instead.');
      return;
    }
    
    setPreviewing(prev => ({ ...prev, [material._id]: true }));

    try {
      const isImage = publicAPI.isImage(material);
      
      if (window.openFilePreview) {
        window.openFilePreview(material);
      } else if (isImage) {
        setShowImageModal(material);
      } else {
        const previewUrl = publicAPI.getPreviewUrl(material._id);
        window.open(previewUrl, '_blank', 'noopener,noreferrer');
      }

      await publicAPI.trackDownload(material._id);

      // Update views
      setMaterials(prev => prev.map(m => 
        m._id === material._id 
          ? { ...m, views: (m.views || 0) + 1 }
          : m
      ));

      // Show notification
      if (window.addPreviewNotification) {
        window.addPreviewNotification(material);
      }

    } catch (e) {
      console.error('Preview failed:', e);
      alert("Preview failed.");
    } finally {
      setTimeout(() => {
        setPreviewing(prev => ({ ...prev, [material._id]: false }));
      }, 1000);
    }
  };

  const toggleMaterialExpansion = (materialId) => {
    setExpandedMaterials(prev => ({ ...prev, [materialId]: !prev[materialId] }));
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${sizes[i]}`;
  };

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      <div className="flex-1 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    </div>
  );

  return (
    <>
      {/* Image Modal */}
      {showImageModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[999999] bg-black flex items-center justify-center p-4"
          onClick={() => setShowImageModal(null)}
        >
          <div className="relative max-w-4xl max-h-[90vh] w-full">
            <button
              onClick={() => setShowImageModal(null)}
              className="absolute top-4 right-4 z-10 p-3 bg-white rounded-full hover:bg-gray-100 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
            
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDownload(showImageModal);
              }}
              className="absolute top-4 left-4 z-10 p-3 bg-white rounded-full hover:bg-gray-100 transition-colors"
            >
              <Download className="w-6 h-6" />
            </button>
            
            <img
              src={publicAPI.getPreviewUrl(showImageModal._id)}
              alt={showImageModal.title}
              className="max-w-full max-h-[90vh] object-contain rounded-lg"
            />
            
            <div className="absolute bottom-4 left-4 right-4 bg-black/50 text-white p-4 rounded-lg backdrop-blur-sm">
              <div className="flex justify-between items-center">
                <div>
                  <span className="font-medium">{showImageModal.title}</span>
                  <div className="text-sm text-white/80 mt-1">
                    {showImageModal.file?.format?.toUpperCase()} • {formatFileSize(showImageModal.file?.size)}
                  </div>
                </div>
                <div className="text-sm">
                  {showImageModal.downloads || 0} downloads
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        <Header />
        
        <div className="container mx-auto px-4 py-12">
          <div className="mb-8">
            <Link 
              to={`/school/${schoolId}/department/${departmentId}/year/${year}/course/${courseId}`}
              className="inline-flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Change Instructor</span>
            </Link>
          </div>
          
          {/* Header Info */}
          <div className="bg-gradient-to-r from-green-600 to-green-800 rounded-2xl p-8 text-white mb-12 shadow-lg">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
              <div className="flex-1">
                <h1 className="text-3xl md:text-4xl font-bold mb-2">{course?.title}</h1>
                <p className="text-green-100 text-lg mb-4">{course?.courseCode} • {course?.credits || 3} Credits</p>
                
                <div className="flex items-center gap-3 bg-white/10 p-3 rounded-lg w-fit backdrop-blur-sm">
                  <div className="bg-white text-green-700 p-1 rounded-full">
                    <User className="w-5 h-5"/>
                  </div>
                  <div>
                    <p className="text-xs text-green-200 uppercase tracking-wider">Materials By</p>
                    <p className="font-bold text-lg">{teacher?.fullName || 'Selected Instructor'}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8 border border-gray-100">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4 mb-6">
              <h2 className="text-2xl font-bold text-gray-900">
                Files ({filteredMaterials.length})
              </h2>
              <div className="relative">
                <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <select 
                  value={filterType} 
                  onChange={(e) => setFilterType(e.target.value)} 
                  className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
                >
                  <option value="all">All Types</option>
                  <option value="lecture">Lectures</option>
                  <option value="assignment">Assignments</option>
                  <option value="exam">Exams</option>
                </select>
              </div>
            </div>
            
            {/* Materials List */}
            <div className="space-y-4">
              {filteredMaterials.length > 0 ? (
                filteredMaterials.map(material => {
                  const isImage = publicAPI.isImage(material);
                  const canPreview = publicAPI.canPreview(material);
                  
                  return (
                    <motion.div 
                      key={material._id} 
                      initial={{ opacity: 0, y: 10 }} 
                      animate={{ opacity: 1, y: 0 }} 
                      className="bg-white border border-gray-200 rounded-lg hover:shadow-md transition-shadow overflow-hidden"
                    >
                      <div className="p-6">
                        <div className="flex flex-col md:flex-row justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-start gap-4">
                              <div className="flex-shrink-0">
                                {isImage ? (
                                  <div className="p-3 bg-green-50 text-green-600 rounded-lg">
                                    <FileImage className="w-6 h-6" />
                                  </div>
                                ) : (
                                  <div className="p-3 bg-blue-50 text-blue-600 rounded-lg">
                                    <FileText className="w-6 h-6" />
                                  </div>
                                )}
                              </div>
                              <div className="flex-1">
                                <h3 className="text-lg font-bold text-gray-900 mb-1">{material.title}</h3>
                                <div className="flex flex-wrap gap-3 text-sm text-gray-500 mb-2">
                                  <span className="flex items-center gap-1">
                                    <Calendar className="w-3 h-3" />
                                    {new Date(material.createdAt).toLocaleDateString()}
                                  </span>
                                  <span className="px-2 py-0.5 bg-gray-100 rounded capitalize text-xs">
                                    {material.type || 'Resource'}
                                  </span>
                                  <span className="text-gray-400">•</span>
                                  <span className="text-gray-500">
                                    {formatFileSize(material.file?.size)}
                                  </span>
                                </div>
                                
                                {/* Image thumbnail for image files */}
                                {isImage && (
                                  <div 
                                    className="mt-3 mb-3 rounded-lg overflow-hidden cursor-pointer w-32 h-24"
                                    onClick={() => handlePreview(material)}
                                  >
                                    <img
                                      src={publicAPI.getPreviewUrl(material._id)}
                                      alt={material.title}
                                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                                      onError={(e) => {
                                        e.target.src = `data:image/svg+xml,${encodeURIComponent(`
                                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
                                            <rect width="100" height="100" fill="#f3f4f6"/>
                                            <text x="50" y="50" font-family="Arial" font-size="12" fill="#9ca3af" text-anchor="middle" dy=".3em">${material.file?.format?.toUpperCase()}</text>
                                          </svg>
                                        `)}`;
                                      }}
                                    />
                                  </div>
                                )}
                                
                                {material.description && (
                                  <div className="mt-2">
                                    <button 
                                      onClick={() => toggleMaterialExpansion(material._id)}
                                      className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium"
                                    >
                                      {expandedMaterials[material._id] ? (
                                        <>Hide Description <ChevronUp className="w-3 h-3" /></>
                                      ) : (
                                        <>Show Description <ChevronDown className="w-3 h-3" /></>
                                      )}
                                    </button>
                                    {expandedMaterials[material._id] && (
                                      <p className="mt-2 text-sm text-gray-600 bg-gray-50 p-3 rounded">
                                        {material.description}
                                      </p>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex flex-col gap-2 min-w-[160px] border-l pl-4 border-gray-100">
                            <div className="text-right text-xs text-gray-500 mb-1">
                              {material.downloads || 0} Downloads
                              {isImage && (
                                <span className="ml-2">• {material.views || 0} Views</span>
                              )}
                            </div>
                            <Button 
                              variant="primary" 
                              size="small" 
                              icon={downloading[material._id] ? FileCheck : Download} 
                              onClick={() => handleDownload(material)} 
                              disabled={downloading[material._id]} 
                              className="w-full justify-center bg-green-600 hover:bg-green-700"
                            >
                              {downloading[material._id] ? 'Downloading...' : 'Download'}
                            </Button>
                            
                            {canPreview && (
                              <Button 
                                variant="outline" 
                                size="small" 
                                icon={previewing[material._id] ? FileSearch : Eye} 
                                onClick={() => handlePreview(material)} 
                                disabled={previewing[material._id]}
                                className="w-full justify-center"
                              >
                                {previewing[material._id] ? 'Opening...' : (isImage ? 'View Image' : 'Preview')}
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  );
                })
              ) : (
                <div className="text-center py-12 bg-gray-50 rounded-lg border border-dashed border-gray-200">
                  <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-1">No materials found</h3>
                  <p className="text-gray-500 text-sm">
                    This instructor hasn't uploaded any {filterType !== 'all' ? filterType : ''} materials for this course yet.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
        <Footer />
      </div>
    </>
  );
};

export default CourseMaterials;